// Post-action verification helpers. After every write, we re-fetch the
// affected entity and compare it against the proposed change so the tool
// output reports the *actual* state, not just the dispatch result.

import type { FetchedField, FetchedSchema } from "./schemaApi";
import type { AssistantFieldInput, AssistantUpdateFieldInput } from "./payload";

export interface VerificationReport {
    verified: boolean;
    appliedChanges: string[];
    unappliedChanges: string[];
}

function normName(s: string | undefined | null): string {
    return (s || "").trim().toLowerCase();
}

function findField(fields: FetchedField[] | undefined, name: string): FetchedField | undefined {
    if (!Array.isArray(fields)) return undefined;
    return fields.find((f) => normName(f.name) === normName(name));
}

function describeField(f: AssistantFieldInput | AssistantUpdateFieldInput): string {
    return (f as any).name || (f as any).fieldName || "(unknown)";
}

export interface CreateProposal {
    name: string;
    description: string;
    fields: AssistantFieldInput[];
}

export function verifyCreatedSchema(proposed: CreateProposal, refreshed: FetchedSchema | null): VerificationReport {
    const applied: string[] = [];
    const unapplied: string[] = [];
    if (!refreshed) {
        return { verified: false, appliedChanges: [], unappliedChanges: ["schema was not found after creation"] };
    }
    if (normName(refreshed.name) === normName(proposed.name)) applied.push(`name: ${proposed.name}`);
    else unapplied.push(`name "${proposed.name}" was not set (server has "${refreshed.name}")`);

    if ((refreshed.description || "") === (proposed.description || "")) applied.push("description");
    else unapplied.push("description was not set as proposed");

    for (const pf of proposed.fields || []) {
        const found = findField(refreshed.fields, pf.name);
        if (!found) {
            unapplied.push(`field "${pf.name}" is missing`);
            continue;
        }
        if (found.dataType !== pf.dataType) {
            unapplied.push(`field "${pf.name}" has dataType "${found.dataType}", expected "${pf.dataType}"`);
            continue;
        }
        applied.push(`field "${pf.name}" (${pf.dataType})`);
    }
    return { verified: unapplied.length === 0, appliedChanges: applied, unappliedChanges: unapplied };
}

export interface UpdateProposal {
    name?: string;
    description?: string;
    addFields: AssistantFieldInput[];
    updateFields: AssistantUpdateFieldInput[];
    deleteFields: Array<{ fieldName: string }>;
}

export function verifyUpdatedSchema(
    before: FetchedSchema,
    proposed: UpdateProposal,
    after: FetchedSchema | null,
): VerificationReport {
    const applied: string[] = [];
    const unapplied: string[] = [];
    if (!after) {
        return { verified: false, appliedChanges: [], unappliedChanges: ["schema could not be re-fetched after update"] };
    }

    if (proposed.name !== undefined && proposed.name !== before.name) {
        if (normName(after.name) === normName(proposed.name)) applied.push(`renamed to "${proposed.name}"`);
        else unapplied.push(`rename to "${proposed.name}" did not apply (server has "${after.name}")`);
    }

    if (proposed.description !== undefined && proposed.description !== before.description) {
        if ((after.description || "") === (proposed.description || "")) applied.push("description updated");
        else unapplied.push("description change did not apply");
    }

    for (const pf of proposed.addFields || []) {
        const found = findField(after.fields, pf.name);
        if (!found) unapplied.push(`new field "${pf.name}" was not added`);
        else if (found.dataType !== pf.dataType) unapplied.push(`field "${pf.name}" was added with wrong dataType "${found.dataType}" (expected "${pf.dataType}")`);
        else applied.push(`added field "${pf.name}" (${pf.dataType})`);
    }

    for (const uf of proposed.updateFields || []) {
        const found = findField(after.fields, uf.newName || uf.fieldName);
        if (!found) {
            unapplied.push(`update to field "${uf.fieldName}" did not apply (not found after save)`);
            continue;
        }
        const mismatches: string[] = [];
        if (uf.newName && normName(found.name) !== normName(uf.newName)) mismatches.push(`name (still "${found.name}")`);
        if (uf.dataType !== undefined && found.dataType !== uf.dataType) mismatches.push(`dataType (still "${found.dataType}")`);
        if (uf.description !== undefined && (found.description || "") !== (uf.description || "")) mismatches.push("description");
        if (uf.isPrimaryKey !== undefined && !!found.isPrimaryKey !== !!uf.isPrimaryKey) mismatches.push("isPrimaryKey");
        if (uf.isSortKey !== undefined && !!found.isSortKey !== !!uf.isSortKey) mismatches.push("isSortKey");
        if (uf.isRequired !== undefined && !!found.isRequired !== !!uf.isRequired) mismatches.push("isRequired");
        if (uf.isPII !== undefined && !!found.isPII !== !!uf.isPII) mismatches.push("isPII");
        if (mismatches.length === 0) applied.push(`updated field "${describeField(uf)}"`);
        else unapplied.push(`changes to field "${uf.fieldName}" did not apply: ${mismatches.join(", ")}`);
    }

    for (const df of proposed.deleteFields || []) {
        const found = findField(after.fields, df.fieldName);
        if (found) unapplied.push(`field "${df.fieldName}" was not deleted (still present)`);
        else applied.push(`deleted field "${df.fieldName}"`);
    }

    return { verified: unapplied.length === 0, appliedChanges: applied, unappliedChanges: unapplied };
}

export function verifyDeletedSchema(refreshed: FetchedSchema | null, schemaName: string): VerificationReport {
    if (refreshed == null) {
        return { verified: true, appliedChanges: [`deleted "${schemaName}"`], unappliedChanges: [] };
    }
    return {
        verified: false,
        appliedChanges: [],
        unappliedChanges: [`"${schemaName}" is still present on the server after the delete call`],
    };
}
